import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Handshake } from "lucide-react";
import { useAI } from "../hooks/useAI";
import { useFeeConfig } from "../hooks/useFeeConfig";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import EvidenceList from "../components/dispute/EvidenceList";
import ProviderPicker from "../components/dispute/ProviderPicker";
import COUNTRIES from "../data/countries";
import { regionsFor } from "../data/regions";
import {
  COLORS,
  SANS,
  JUDGE_COLOR,
  formStyles,
  pageStyles,
} from "../components/dispute/theme";
import {
  makeEmptyEvidenceRow,
  filterValidEvidence,
  validateEvidenceCap,
  validateEvidenceInfoRequired,
} from "../components/dispute/evidence";
import { saveDraft, getDraft, clearDraft } from "../lib/draftCache";

const API_BASE = process.env.REACT_APP_API_URL || "";
const MAX_WITNESSES = 2;

const emptyParticipants = () => [{ id: Date.now(), email: "", role: "disputant" }];

export default function SubmitDispute() {
  const navigate = useNavigate();
  const { providers, fetchProviders, fetchProviderModels, createDraftDispute, saveParticipants, submitFeedback } = useAI();
  const fee = useFeeConfig();

  // Restored once on mount (e.g. after clicking "Back" from the checkout page)
  // so the values typed before leaving are still here, still editable.
  const restoredRef = useRef(getDraft());
  const restored = restoredRef.current;
  const [restoredNotice, setRestoredNotice] = useState(!!restored);

  const [subject, setSubject] = useState(restored?.subject ?? "");
  const [disputeCategoryId, setDisputeCategoryId] = useState(restored?.disputeCategoryId ?? "");
  const [jurisdictionState, setJurisdictionState] = useState(restored?.jurisdictionState ?? "");
  const [jurisdictionRegion, setJurisdictionRegion] = useState(restored?.jurisdictionRegion ?? "");
  const [selectedProviders, setSelectedProviders] = useState(restored?.selectedProviders ?? []);
  const [models, setModels] = useState(restored?.models ?? {});
  // Admin-configured selectable models per provider (null → ProviderPicker uses bundled defaults).
  const [providerModels, setProviderModels] = useState(null);
  // Admin-configured default (pre-selected) model per provider.
  const [providerDefaults, setProviderDefaults] = useState(null);
  const [prompt, setPrompt] = useState(restored?.prompt ?? "");
  const [desiredOutcome, setDesiredOutcome] = useState(restored?.desiredOutcome ?? "");
  const [evidence, setEvidence] = useState(restored?.evidence ?? [makeEmptyEvidenceRow()]);
  const [categories, setCategories] = useState([]);
  const [participantsList, setParticipantsList] = useState(restored?.participantsList ?? emptyParticipants());
  const [processFeedback, setProcessFeedback] = useState(restored?.processFeedback ?? "");
  // Publish the final decision page once the panel has ruled. Off by default —
  // a case is private to its parties unless the filer opts in.
  const [isPublic, setIsPublic] = useState(restored?.isPublic ?? false);
  const [submitting, setSubmitting] = useState(false);

  // Keep the cache mirroring the live form so it's always up to date whenever
  // the user navigates away (Back button, or anywhere else).
  useEffect(() => {
    saveDraft({
      subject,
      disputeCategoryId,
      jurisdictionState,
      jurisdictionRegion,
      selectedProviders,
      models,
      prompt,
      desiredOutcome,
      evidence,
      participantsList,
      processFeedback,
      isPublic,
    });
  }, [subject, disputeCategoryId, jurisdictionState, jurisdictionRegion, selectedProviders, models, prompt, desiredOutcome, evidence, participantsList, processFeedback, isPublic]);

  const clearForm = () => {
    setSubject("");
    setDisputeCategoryId("");
    setJurisdictionState("");
    setJurisdictionRegion("");
    setSelectedProviders([]);
    setModels({});
    setPrompt("");
    setDesiredOutcome("");
    setEvidence([makeEmptyEvidenceRow()]);
    setParticipantsList(emptyParticipants());
    setProcessFeedback("");
    setIsPublic(false);
    clearDraft();
    setRestoredNotice(false);
  };

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    fetchProviderModels().then((cfg) => {
      if (cfg?.models) {
        setProviderModels(cfg.models);
        setProviderDefaults(cfg.defaults || null);
      }
    });
  }, [fetchProviderModels]);

  useEffect(() => {
    fetch(`${API_BASE}/api/ai/categories`)
      .then((r) => r.json())
      .then((d) => setCategories(d.categories || []))
      .catch(() => setCategories([]));
  }, []);

  // New rows are witnesses — there can only ever be one disputant (seeded as
  // the first row). If the disputant row was removed, a row can be promoted
  // back to disputant via the dropdown.
  const witnessCount = participantsList.filter((p) => p.role === "witness").length;
  const witnessAtCap = witnessCount >= MAX_WITNESSES;

  const addParticipantRow = () => {
    if (witnessAtCap) return;
    setParticipantsList((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), email: "", role: "witness", witnessInfo: "" },
    ]);
  };
  const removeParticipantRow = (id) =>
    setParticipantsList((prev) => prev.filter((p) => p.id !== id));
  const updateParticipantRow = (id, field, value) =>
    setParticipantsList((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );

  // Selection is constrained to exactly 1 AI (solo evaluation, no judge) or
  // every available AI (multi + judge required). Anything in between is invalid.
  const availableCount = providers.length;
  const isSolo = selectedProviders.length === 1;
  const isAll = availableCount > 0 && selectedProviders.length === availableCount;
  const canRun = !submitting && prompt.trim() && (isSolo || isAll);

  const handleRun = async () => {
    if (!canRun) return;

    const validEvidence = filterValidEvidence(evidence);
    const overMsg = validateEvidenceCap(validEvidence);
    if (overMsg) {
      alert(overMsg);
      return;
    }
    const infoMsg = validateEvidenceInfoRequired(validEvidence);
    if (infoMsg) {
      alert(infoMsg);
      return;
    }
    const missingWitnessInfo = participantsList.some(
      (p) => p.role === "witness" && p.email.trim() && !(p.witnessInfo || "").trim()
    );
    if (missingWitnessInfo) {
      alert("Please explain how each witness relates to the dispute.");
      return;
    }

    // Capture which AIs evaluate so the server can run the panel after payment.
    const runConfig = {
      providers: selectedProviders.map((p) => ({ provider: p, model: models[p] || null })),
    };

    setSubmitting(true);
    try {
      // 1. Save the case as a draft (no AI runs yet).
      const draft = await createDraftDispute({
        subject,
        prompt,
        desiredOutcome: desiredOutcome.trim() || null,
        disputeCategoryId: disputeCategoryId ? Number(disputeCategoryId) : null,
        jurisdictionState: jurisdictionState.trim() || null,
        jurisdictionRegion: jurisdictionRegion.trim() || null,
        evidence: validEvidence,
        runConfig,
        isPublic,
      });

      if (draft.error || !draft.uuid) {
        alert(draft.error || "Failed to create dispute. Please try again.");
        return;
      }

      // 2. Record invited participants now — invitations are emailed once the
      //    filing fee is paid, not here.
      const validParticipants = participantsList
        .filter((p) => p.email.trim() && p.email.includes("@"))
        .map((p) => ({
          email: p.email.trim(),
          role: p.role,
          witnessInfo: p.role === "witness" ? p.witnessInfo?.trim() || null : null,
        }));
      if (validParticipants.length > 0) {
        await saveParticipants(draft.queryId, validParticipants, { sendEmail: false }).catch(
          (err) => console.warn("Saving participants failed:", err)
        );
      }

      // 2b. Optional process feedback — best-effort, never blocks filing.
      if (processFeedback.trim()) {
        submitFeedback("process", processFeedback.trim()).catch(
          (err) => console.warn("Saving process feedback failed:", err)
        );
      }

      // 3. Off to checkout — paying the filing fee is what triggers the panel.
      navigate(`/checkout/${draft.uuid}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageStyles.root}>
      <SiteHeader />
      <div style={pageStyles.shell}>
        <div style={styles.stack}>
          {/* ── Header card — background color test (was COLORS.dark navy).
              Round 1: invite email's cream (#f3ede2, see buildDisputeEmail in
              backend/src/routes/ai.js). Round 2: light turquoise tint
              rgba(64,224,208,0.16), then 0.08 (lighter). Round 3 (current):
              white + 1px turquoise (#14A0C8) border. The "File the Dispute"
              button below now matches this same turquoise too (was
              COLORS.dark, excluded from earlier rounds, now included). ── */}
          <div style={styles.headerCard}>
            <div style={styles.headerRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ ...pageStyles.pageTitle, color: "#1A5EC4", margin: 0 }}>
                  File your dispute
                </h1>
                <p style={{ ...pageStyles.pageSubtitle, color: COLORS.inkSoft, margin: "8px 0 0 0" }}>
                  Tell us about the dispute. The AI Neutral(s) will return a decision that
                  will also be shared with the other party.
                </p>
              </div>
              <Handshake size={72} strokeWidth={1.5} style={styles.headerIcon} aria-hidden />
            </div>
          </div>

          {restoredNotice && (
            <div style={styles.restoredBanner}>
              <span>We restored what you'd already entered.</span>
              <button type="button" style={styles.restoredClearBtn} onClick={clearForm}>
                Clear form
              </button>
            </div>
          )}

          {/* ── Section 1: dispute classification ── */}
          <div style={styles.sectionCard}>
            <div style={formStyles.fieldGroup}>
              <h2 style={formStyles.sectionTitle}>Dispute Subject</h2>
              <input
                type="text"
                style={formStyles.textInput}
                placeholder="A short title for the dispute"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div style={formStyles.fieldGroup}>
              <h2 style={formStyles.sectionTitle}>Dispute Category</h2>
              <select
                style={formStyles.select}
                value={disputeCategoryId}
                onChange={(e) => setDisputeCategoryId(e.target.value)}
              >
                <option value="">— Select a category —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ ...formStyles.fieldGroup, marginBottom: 0 }}>
              <h2 style={formStyles.sectionTitle}>Location</h2>
              <div style={formStyles.rowGroup}>
                <div style={formStyles.rowCol}>
                  <label style={formStyles.fieldLabel}>Country</label>
                  <select
                    style={formStyles.select}
                    value={jurisdictionState}
                    onChange={(e) => {
                      setJurisdictionState(e.target.value);
                      // Region options depend on the country; clear any prior
                      // value so a stale region can't carry across countries.
                      setJurisdictionRegion("");
                    }}
                  >
                    <option value="">— Select a country —</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={formStyles.rowCol}>
                  <label style={formStyles.fieldLabel}>Region</label>
                  {regionsFor(jurisdictionState) ? (
                    <select
                      style={formStyles.select}
                      value={jurisdictionRegion}
                      onChange={(e) => setJurisdictionRegion(e.target.value)}
                    >
                      <option value="">— Select a region —</option>
                      {regionsFor(jurisdictionState).map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      style={formStyles.textInput}
                      placeholder="e.g. California, Bucharest"
                      value={jurisdictionRegion}
                      onChange={(e) => setJurisdictionRegion(e.target.value)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 2: narrative + desired outcome ── */}
          <div style={styles.sectionCard}>
            <div style={formStyles.fieldGroup}>
              <h2 style={formStyles.sectionTitle}>Written narrative testimony</h2>
              <textarea
                style={formStyles.textarea}
                placeholder="Explain the dispute and provide a detailed testimony of your perspective."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={6}
              />
            </div>

            <div style={{ ...formStyles.fieldGroup, marginBottom: 0 }}>
              <h2 style={formStyles.sectionTitle}>Desired outcome</h2>
              <textarea
                style={formStyles.textarea}
                placeholder="What resolution are you looking for? (e.g. refund, apology, contract change)"
                value={desiredOutcome}
                onChange={(e) => setDesiredOutcome(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* ── Section 3: evidence ── */}
          <div style={styles.sectionCard}>
            <EvidenceList evidence={evidence} setEvidence={setEvidence} title="Evidence" />
          </div>

          {/* ── Section 4: invite participants ── */}
          <div style={styles.sectionCard}>
            <div style={{ ...formStyles.fieldGroup, marginBottom: 0 }}>
              <h2 style={formStyles.sectionTitle}>Invite participants</h2>
              <p style={styles.hint}>
                Send a private link to the other party. They'll see the dispute and can
                add their response. You can also invite witnesses to provide extra
                information.
              </p>
              {participantsList.map((p) => {
                // Only one disputant is allowed: a row may pick "Disputant" only
                // if it's already the disputant, or no other row has claimed it.
                const disputantTaken = participantsList.some(
                  (x) => x.id !== p.id && x.role === "disputant"
                );
                return (
                  <div key={p.id} style={styles.participantRowWrap}>
                    <div style={styles.participantRow}>
                      <input
                        type="email"
                        placeholder="email@example.com"
                        style={{ ...formStyles.textInput, flex: 1 }}
                        value={p.email}
                        onChange={(e) => updateParticipantRow(p.id, "email", e.target.value)}
                      />
                      <select
                        style={{ ...formStyles.select, width: 140 }}
                        value={p.role}
                        onChange={(e) => updateParticipantRow(p.id, "role", e.target.value)}
                      >
                        {!disputantTaken && <option value="disputant">Disputant</option>}
                        <option value="witness">Witness</option>
                      </select>
                      {participantsList.length > 1 && (
                        <button
                          type="button"
                          style={styles.removeBtn}
                          onClick={() => removeParticipantRow(p.id)}
                          title="Remove"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {p.role === "witness" && (
                      <textarea
                        style={{ ...formStyles.textarea, marginTop: 8 }}
                        placeholder="Please briefly explain how this witness is related to the dispute. (required)"
                        value={p.witnessInfo || ""}
                        onChange={(e) => updateParticipantRow(p.id, "witnessInfo", e.target.value)}
                        maxLength={500}
                        rows={2}
                        required
                      />
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                style={{
                  ...styles.addParticipantBtn,
                  opacity: witnessAtCap ? 0.5 : 1,
                  cursor: witnessAtCap ? "not-allowed" : "pointer",
                }}
                onClick={addParticipantRow}
                disabled={witnessAtCap}
                title={witnessAtCap ? `Maximum ${MAX_WITNESSES} witnesses per dispute` : ""}
              >
                + Add Witness {witnessAtCap ? `(${MAX_WITNESSES}/${MAX_WITNESSES})` : `(${witnessCount}/${MAX_WITNESSES})`}
              </button>
            </div>
          </div>

          {/* ── Section 5: publish the decision (optional) ── */}
          <div style={styles.sectionCard}>
            <div style={{ ...formStyles.fieldGroup, marginBottom: 0 }}>
              <h2 style={formStyles.sectionTitle}>Publish the decision <span style={styles.optionalLabel}>Optional</span></h2>
              <p style={styles.hint}>
                Off by default — only you and the parties you invite can read this
                case. Turn it on and, once the Neutrals have ruled, anyone with the
                link can read the decision page. Names, email addresses and uploaded
                evidence files are never published.
              </p>
              <label style={styles.publicToggle}>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                />
                <span>Make the final decision page public</span>
              </label>
            </div>
          </div>

          {/* ── Section 6: process feedback (optional) ── */}
          <div style={styles.sectionCard}>
            <div style={{ ...formStyles.fieldGroup, marginBottom: 0 }}>
              <h2 style={formStyles.sectionTitle}>Feedback on this process <span style={styles.optionalLabel}>Optional</span></h2>
              <p style={styles.hint}>
                Anything confusing, frustrating, or missing while filing this dispute? We read every note.
              </p>
              <textarea
                style={formStyles.textarea}
                placeholder="Tell us about your experience filing this dispute so far."
                value={processFeedback}
                onChange={(e) => setProcessFeedback(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* ── Section 7: AI providers + run (last) ── */}
          <div style={styles.sectionCard}>
            <ProviderPicker
              providers={providers}
              selectedProviders={selectedProviders}
              setSelectedProviders={setSelectedProviders}
              models={models}
              setModels={setModels}
              providerModels={providerModels}
              providerDefaults={providerDefaults}
              title="AI Neutrals"
              hint={`Pick one or three AI Neutrals for the decision. One Neutral is free while a three Neutral decision is ${fee.label}. Each Neutral is a different LLM model.`}
            />

            {isSolo && (
              <div style={{ ...styles.providersNote, marginBottom: 0 }}>
                <span style={styles.providersIcon}>🤖</span>
                <span>
                  <strong>{selectedProviders[0]}</strong> is enabled for evaluation.
                  You can leave it as the solo evaluator, or pick another AI to
                  enable all of them.
                </span>
              </div>
            )}

            {isAll && (
              <div style={{ ...styles.providersNote, marginBottom: 12 }}>
                <span style={styles.providersIcon}>🤖</span>
                <span>
                  <strong>{selectedProviders.length} AI Neutrals</strong> enabled for
                  the evaluation.
                </span>
              </div>
            )}

            {isAll && (
              <div style={{ ...styles.judgeNote, marginBottom: 0 }}>
                <span style={styles.judgeIcon}>⚖</span>
                <span>
                  A decision is automatically rendered after whichever happens first:
                  all parties (disputants and witnesses) have submitted their
                  information; the end of the seven day deadline period.
                </span>
              </div>
            )}

            <button
              type="button"
              style={{
                ...formStyles.primaryButton,
                // Color test (see styles.headerCard above): match the header
                // card's turquoise border instead of the default navy, only
                // while enabled — the disabled gray state is untouched.
                ...(canRun ? { background: "#1A5EC4", color: "#fff" } : formStyles.primaryButtonDisabled),
                marginTop: 24,
              }}
              onClick={handleRun}
              disabled={!canRun}
            >
              {submitting
                ? "Filing…"
                : `File the Dispute (${selectedProviders.length} provider${selectedProviders.length !== 1 ? "s" : ""})`}
            </button>
            {!canRun && !submitting && (
              <p style={styles.disabledHint}>
                {!prompt.trim()
                  ? "Add your testimony to continue."
                  : selectedProviders.length === 0
                  ? "Pick one AI for solo evaluation, or all of them for a judged verdict."
                  : ""}
              </p>
            )}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  stack: {
    width: "100%",
    maxWidth: 760,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  sectionCard: {
    width: "100%",
    background: COLORS.card,
    borderRadius: "20px 20px 20px 4px",
    padding: "32px 40px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.06)",
    border: `1px solid ${COLORS.border}`,
  },
  headerCard: {
    width: "100%",
    background: "#fff",
    color: COLORS.ink,
    borderRadius: "20px 20px 20px 4px",
    padding: "32px 40px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.10)",
    border: "2px solid rgba(26,94,196,0.2)",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: 24,
  },
  headerIcon: {
    color: "#1A5EC4",
    flexShrink: 0,
    opacity: 0.9,
  },
  restoredBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 16px",
    background: COLORS.highlight,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 10,
    fontSize: 13,
    color: COLORS.ink,
  },
  restoredClearBtn: {
    background: "transparent",
    border: `1px solid ${COLORS.borderStrong}`,
    color: COLORS.ink,
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 12,
    fontFamily: SANS,
    cursor: "pointer",
    flexShrink: 0,
  },
  judgeNote: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: "#fdf6da",
    border: `1px solid ${JUDGE_COLOR}`,
    borderRadius: 10,
    fontSize: 13,
    color: COLORS.ink,
    marginBottom: 24,
    lineHeight: 1.5,
  },
  judgeIcon: { fontSize: 20, flexShrink: 0 },
  providersNote: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    background: COLORS.highlight,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 10,
    fontSize: 13,
    color: COLORS.ink,
    marginBottom: 12,
    lineHeight: 1.5,
  },
  providersIcon: { fontSize: 20, flexShrink: 0 },
  hint: { color: COLORS.inkSoft, fontSize: 12, marginBottom: 12, lineHeight: 1.5, margin: "0 0 12px 0" },
  optionalLabel: { fontSize: 11, fontWeight: 400, color: COLORS.inkMuted, marginLeft: 6 },
  publicToggle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: COLORS.ink,
    cursor: "pointer",
  },
  participantRowWrap: { marginBottom: 8 },
  participantRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 0 },
  removeBtn: {
    background: "#ff4d4d18",
    border: "1px solid #ff4d4d",
    color: "#cc3333",
    borderRadius: 4,
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 18,
    padding: 0,
  },
  addParticipantBtn: {
    background: "transparent",
    border: `1px dashed ${COLORS.borderStrong}`,
    color: COLORS.inkSoft,
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 12,
    width: "100%",
    fontFamily: SANS,
    cursor: "pointer",
    marginTop: 4,
  },
  disabledHint: {
    color: COLORS.inkMuted,
    fontSize: 12,
    margin: "10px 0 0 0",
    textAlign: "center",
  },
};
